import { NotificationType } from '~/types/notifications'
import { useNotificationsStore } from '~/stores/notifications'

interface UseCartNotificationOptions {
  openDelay?: number
  closeDelay?: number
  enableTimerManagement?: boolean
}

export const useCartNotification = (options: UseCartNotificationOptions = {}) => {
  const {
    openDelay = 500,
    closeDelay = 300,
    enableTimerManagement = true
  } = options

  const route = useRoute()
  const notificationsStore = useNotificationsStore()

  // Timer management
  const openTimer = ref<NodeJS.Timeout | null>(null)
  const closeTimer = ref<NodeJS.Timeout | null>(null)

  // Utility to clear timers – prevents multiple setTimeout leaks
  const clearTimers = () => {
    if (!enableTimerManagement) { return }

    if (openTimer.value) {
      clearTimeout(openTimer.value)
      openTimer.value = null
    }
    if (closeTimer.value) {
      clearTimeout(closeTimer.value)
      closeTimer.value = null
    }
  }

  // Hover handler – starts a timer before showing the cart notification.
  // This delay lets the user click the cart icon without immediately hiding it.
  const openCartStateNotification = () => {
    clearTimers()

    const timerFunction = () => {
      // Check if current route is blacklisted before showing notification
      const currentRouteName = route.name
      const badRoute = notificationsStore.isRouteBlacklistedForNotifications(currentRouteName)

      if (!badRoute) {
        notificationsStore.setHovered(true, 'cart')
        notificationsStore.showNotification({
          notification: {
            type: NotificationType.CartState,
            props: {}
          },
          // force(true) ensures this drawer appears even if another is open
          force: true
        })
      }
    }

    if (enableTimerManagement) {
      openTimer.value = setTimeout(timerFunction, openDelay)
    } else {
      setTimeout(timerFunction, openDelay)
    }
  }

  // Mouseleave handler – waits before hiding so that
  // the user can move into the flyout drawer without it disappearing.
  const closeCartStateNotification = () => {
    clearTimers()

    const closeFunction = () => {
      notificationsStore.setHovered(false, 'cart')
    }

    if (enableTimerManagement) {
      closeTimer.value = setTimeout(closeFunction, closeDelay)
    } else {
      setTimeout(closeFunction, closeDelay)
    }
  }

  // Cleanup function to clear timers when component unmounts
  const cleanup = () => {
    clearTimers()
  }

  return {
    openCartStateNotification,
    closeCartStateNotification,
    clearTimers,
    cleanup
  }
}
